import { useState } from "react";
import { MapPin } from "lucide-react";
import { site } from "../../lib/siteConfig";

// Map of city → stock photo URL.
// Solo Milano e Roma sono verificate. Le altre sono in attesa di foto definitive.
const verifiedFallback = "https://images.pexels.com/photos/5771259/pexels-photo-5771259.jpeg";
const cityImages = {
  Milano: "https://images.pexels.com/photos/5771259/pexels-photo-5771259.jpeg",
  Monza: "https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/m8me0swc_Centro%20Storico.png",
  Como: "https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/v0dsa0y7_COMO.webp",
  Bergamo: "https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/0ty3olr1_jk-bb-bergamo-7147856.jpg",
  Brescia: "https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/zrr80w2y_BRESCOA.jpg",
  "Lago di Iseo": "https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/isi6zzkg_ISEO.jpg",
  "Lago di Garda": "https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/be8srtit_lago%20di%20garda.jpg",
  Roma: "https://customer-assets.emergentagent.com/job_transitorio-milano/artifacts/ibmd3lbn_ROMA%20%282%29.jpg",
};

const fallback = verifiedFallback;

export const Coverage = () => {
  const [active, setActive] = useState("Milano");

  return (
    <section
      id="dove-lavoro"
      data-testid="coverage-section"
      className="relative bg-brand-bg py-24 sm:py-32 lg:py-40 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6">
            <p className="divider-ornament" data-testid="coverage-eyebrow">Dove lavoro</p>
            <h2
              data-testid="coverage-headline"
              className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] font-bold text-brand-ink mt-8"
            >
              Milano al centro.<br />
              <span className="italic font-normal">L'Italia tutto attorno.</span>
            </h2>
            <p
              data-testid="coverage-paragraph"
              className="mt-8 text-lg leading-relaxed text-brand-muted max-w-xl"
            >
              Il cuore della mia attività è <span className="text-brand-ink font-medium">Milano</span>, la città in cui sono cresciuta.
              Seguo inoltre proprietari nelle principali città italiane, sempre con lo stesso metodo e la stessa attenzione.
            </p>
            <p className="mt-3 text-sm text-brand-muted italic">
              Clicca su una città per scoprirla.
            </p>

            <ul
              data-testid="coverage-cities"
              className="mt-8 flex flex-wrap gap-2 max-w-2xl"
            >
              {site.cities.map((c) => {
                const isActive = active === c;
                return (
                  <li key={c}>
                    <button
                      type="button"
                      onClick={() => setActive(c)}
                      data-testid={`coverage-city-${c.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium border transition-all ${
                        isActive
                          ? "bg-brand-ink text-white border-brand-ink shadow-sm"
                          : "bg-white text-brand-text border-brand-line hover:border-brand-ink hover:text-brand-ink"
                      }`}
                    >
                      <MapPin size={14} strokeWidth={1.8} />
                      {c}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-4 bg-brand-beige/40 -z-10" />
              <div className="relative w-full aspect-[4/5] overflow-hidden rounded-sm shadow-lg bg-brand-beige/30">
                {site.cities.map((c) => (
                  <img
                    key={c}
                    src={cityImages[c] || fallback}
                    alt={c}
                    data-testid={`coverage-image-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                      active === c ? "opacity-100" : "opacity-0"
                    }`}
                  />
                ))}
              </div>
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm border border-brand-line px-6 py-5 rounded-sm">
                <div className="text-xs uppercase tracking-[0.22em] text-brand-muted">
                  {active === "Milano" ? "Base operativa" : "Anche a"}
                </div>
                <div
                  data-testid="coverage-image-label"
                  className="font-display text-2xl text-brand-ink mt-1"
                >
                  {active}, Italia
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
